/**
 * What a store spends money on (`09-reports.md` § 13).
 *
 * The store costs report is the one report whose inputs a merchant types in
 * rather than the till producing them, and the source states the taxonomy
 * verbatim *(verified — "Marketing Costs Entry", "Labor Costs Entry",
 * "Administrative Costs Entry", "Other Costs Entry" and every line under
 * them)*. So it is a catalogue rather than free text: two months of entries
 * spelled "Electricity" and "electric bill" cannot be added up, and a merchant
 * who cannot compare this month against last has no report at all.
 *
 * Every label is a message key. These are the words a Cambodian owner reads at
 * the end of the month, and "Property Fee" is not one of them.
 *
 * Supply costs are **not** here. They come from the inventory ledger — what
 * the sold items actually drew from stock — and a merchant who could also type
 * them in by hand would be double-counting the largest cost in the report.
 */

export const COST_CATEGORIES = ["marketing", "labor", "administrative", "ad_hoc"] as const;
export type CostCategory = (typeof COST_CATEGORIES)[number];

export type CostType = {
  readonly code: CostTypeCode;
  readonly category: CostCategory;
  readonly labelKey: string;
};

const DEFINITIONS = [
  // Marketing
  { code: "platform_fee", category: "marketing", labelKey: "costs.type.platformFee" },
  { code: "advertising", category: "marketing", labelKey: "costs.type.advertising" },
  { code: "marketing_discounts", category: "marketing", labelKey: "costs.type.marketingDiscounts" },
  { code: "delivery_fee", category: "marketing", labelKey: "costs.type.deliveryFee" },
  { code: "marketing_service_fee", category: "marketing", labelKey: "costs.type.serviceFee" },

  // Labor
  { code: "salary", category: "labor", labelKey: "costs.type.salary" },
  { code: "sales_commission", category: "labor", labelKey: "costs.type.salesCommission" },
  { code: "overtime", category: "labor", labelKey: "costs.type.overtime" },
  { code: "insurance", category: "labor", labelKey: "costs.type.insurance" },
  { code: "benefits_training", category: "labor", labelKey: "costs.type.benefitsTraining" },
  { code: "accommodation", category: "labor", labelKey: "costs.type.accommodation" },
  { code: "transportation", category: "labor", labelKey: "costs.type.transportation" },
  { code: "travel", category: "labor", labelKey: "costs.type.travel" },

  // Administrative
  { code: "rent", category: "administrative", labelKey: "costs.type.rent" },
  { code: "property_fee", category: "administrative", labelKey: "costs.type.propertyFee" },
  { code: "water", category: "administrative", labelKey: "costs.type.water" },
  { code: "electricity", category: "administrative", labelKey: "costs.type.electricity" },
  { code: "gas", category: "administrative", labelKey: "costs.type.gas" },
  { code: "renovation", category: "administrative", labelKey: "costs.type.renovation" },
  {
    code: "equipment_purchase",
    category: "administrative",
    labelKey: "costs.type.equipmentPurchase",
  },
  { code: "equipment_repair", category: "administrative", labelKey: "costs.type.equipmentRepair" },
  { code: "depreciation", category: "administrative", labelKey: "costs.type.depreciation" },

  // Ad hoc — the one-off the taxonomy above did not anticipate. It carries the
  // merchant's own note, which is why it is one code and not twenty.
  { code: "ad_hoc", category: "ad_hoc", labelKey: "costs.type.adHoc" },
] as const satisfies readonly { code: string; category: CostCategory; labelKey: string }[];

export type CostTypeCode = (typeof DEFINITIONS)[number]["code"];

export const COST_TYPES: readonly CostType[] = DEFINITIONS;

export const COST_TYPE_CODES = DEFINITIONS.map((t) => t.code) as unknown as readonly [
  CostTypeCode,
  ...CostTypeCode[],
];

const BY_CODE = new Map<string, CostType>(DEFINITIONS.map((t) => [t.code, t]));

export function isCostTypeCode(value: string): value is CostTypeCode {
  return BY_CODE.has(value);
}

export function costType(code: CostTypeCode): CostType {
  const found = BY_CODE.get(code);
  if (!found) throw new Error(`Unknown cost type: ${code}`);
  return found;
}

/** The category a code belongs to, so a caller cannot file rent under labour. */
export function categoryOf(code: CostTypeCode): CostCategory {
  return costType(code).category;
}
